import type { TemplateFiles } from './template-files'
import {
  TEMPLATE_IDS,
  type TemplateTheme,
  type Theme,
  displayFontOf,
  templateThemeOf,
} from './template-theme'

/** The shipped font families and their `@font-face` rules. */
export interface FontFaces {
  readonly families: readonly string[]
  /** The `@font-face` rules of the shipped families among `families`, their files resolved. */
  cssFor(families: readonly string[]): Promise<string>
}

/** One library template, parsed once: its slides by layout, its styles and its own colours. */
export interface TemplateDoc {
  readonly id: string
  readonly name: string
  readonly summary: string
  readonly root: HTMLElement
  readonly slides: Readonly<Record<string, HTMLElement>>
  /** The text of the template's `<style>` elements. */
  readonly styles: string
  readonly theme: TemplateTheme
  /** The first family of the template's display font. */
  readonly displayFont: string
  /** The shipped families the template names. */
  readonly fontFamilies: readonly string[]
  readonly fontFaces: FontFaces
}

/** The brand dress of a slide: its theme tokens and the brand marks. */
export interface SlideFill {
  readonly theme: Theme
  readonly brandName: string
  readonly logo?: string
}

/** The 17 templates and the font families the bundle ships. */
export interface TemplateLibrary {
  readonly templates: TemplateDoc[]
  readonly fonts: string[]
  readonly fontFaces: FontFaces
}

export const ASSET_URLS = [/\/__templates\/(assets\/[^"'()\s]+)/g, /\/__(fonts\/[^"'()\s]+)/g]
const FONT_FILE_URL = /(?<=url\(')\/(fonts\/[^']+)(?=')/g
const FONT_FACE_RULE = /@font-face\s*\{[^}]*\}/g
const FONT_FACE_FAMILY = /font-family:\s*'([^']+)'/
export const BASE_STYLE =
  'html,body{margin:0;width:1080px;height:1350px;overflow:hidden;background:transparent}'

/** `texts` with every path `patterns` capture replaced by its object URL, each path read once. */
export async function resolveUrls(
  texts: readonly string[],
  patterns: readonly RegExp[],
  files: TemplateFiles,
): Promise<string[]> {
  const paths = new Set(
    texts.flatMap((text) =>
      patterns.flatMap((pattern) => Array.from(text.matchAll(pattern), (match) => match[1] ?? '')),
    ),
  )
  const urls = new Map(
    await Promise.all(
      [...paths].map(async (path) => [path, await files.readObjectUrl(path)] as const),
    ),
  )
  return texts.map((text) =>
    patterns.reduce(
      (out, pattern) => out.replace(pattern, (whole, path: string) => urls.get(path) ?? whole),
      text,
    ),
  )
}

/** The families in the bundle's `fonts.json`. */
export function fontFamiliesOf(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((entry: unknown) =>
    typeof entry === 'object' &&
    entry !== null &&
    'family' in entry &&
    typeof entry.family === 'string'
      ? [entry.family]
      : [],
  )
}

/** The `@font-face` rules of `fontsCss` by family, each family's files resolved once on demand. */
export function fontFacesOf(
  fontsCss: string,
  families: readonly string[],
  files: TemplateFiles,
): FontFaces {
  const rules = new Map<string, string[]>()
  for (const [rule] of fontsCss.matchAll(FONT_FACE_RULE)) {
    const family = FONT_FACE_FAMILY.exec(rule)?.[1]?.toLowerCase()
    if (family !== undefined) rules.set(family, [...(rules.get(family) ?? []), rule])
  }
  const resolved = new Map<string, Promise<string>>()
  function familyCss(family: string): Promise<string> {
    const cached = resolved.get(family)
    if (cached) return cached
    const promise = resolveUrls(rules.get(family) ?? [], [FONT_FILE_URL], files).then((css) =>
      css.join('\n'),
    )
    resolved.set(family, promise)
    promise.catch(() => resolved.delete(family))
    return promise
  }
  return {
    families,
    async cssFor(wanted) {
      const keys = [...new Set(wanted.map((family) => family.toLowerCase()))]
      const css = await Promise.all(keys.filter((key) => rules.has(key)).map(familyCss))
      return css.join('\n')
    },
  }
}

/** Parses one template file, without its scripts; nothing reaches the app document. */
export function parseTemplate(id: string, html: string, fontFaces: FontFaces): TemplateDoc {
  const theme = templateThemeOf(html)
  const parsed = new DOMParser().parseFromString(html, 'text/html')
  for (const script of parsed.querySelectorAll('script')) script.remove()
  const root = parsed.querySelector<HTMLElement>('[data-name]')
  if (!root || theme === null) throw new Error(`template ${id} is not a contract v2 template`)
  const styles = Array.from(parsed.querySelectorAll('style'), (style) => style.textContent).join(
    '\n',
  )
  const slides: Record<string, HTMLElement> = {}
  for (const slide of root.querySelectorAll<HTMLElement>('.slide')) {
    slide.remove()
    const { layout } = slide.dataset
    if (layout && !(layout in slides)) slides[layout] = slide
  }
  const { name = id, summary = '' } = root.dataset
  const fontFamilies = fontFaces.families.filter(
    (family) => html.includes(`"${family}"`) || html.includes(`'${family}'`),
  )
  return {
    id,
    name,
    summary,
    root,
    slides,
    styles,
    theme,
    displayFont: displayFontOf(html),
    fontFamilies,
    fontFaces,
  }
}

/** The bundle's shipped font families and their `@font-face` rules, read from `fonts.css` and `fonts.json`. */
export async function loadFontFaces(files: TemplateFiles): Promise<FontFaces> {
  const [css, json] = await Promise.all([files.readText('fonts.css'), files.readText('fonts.json')])
  return fontFacesOf(css, fontFamiliesOf(JSON.parse(json)), files)
}

/** Loads all 17 templates from `files`, their assets and the fonts they use resolved to object URLs. */
export async function loadTemplateLibrary(files: TemplateFiles): Promise<TemplateLibrary> {
  const [htmls, fontFaces] = await Promise.all([
    Promise.all(TEMPLATE_IDS.map((id) => files.readText(`templates/${id}.html`))),
    loadFontFaces(files),
  ])
  const resolved = await resolveUrls(htmls, ASSET_URLS, files)
  const templates = resolved.map((html, index) =>
    parseTemplate(TEMPLATE_IDS[index] ?? '', html, fontFaces),
  )
  await fontFaces.cssFor(templates.flatMap((doc) => doc.fontFamilies))
  return { templates, fonts: [...fontFaces.families], fontFaces }
}

/** Puts the brand theme tokens and marks of `fill` on a live template root. */
export function applySlideFill(root: HTMLElement, fill: SlideFill): void {
  for (const property of Array.from(root.style))
    if (property.startsWith('--theme-')) root.style.removeProperty(property)
  for (const [token, value] of Object.entries(fill.theme)) root.style.setProperty(token, value)
  for (const el of root.querySelectorAll('[data-slot="brandName"]')) el.textContent = fill.brandName
  if (fill.logo !== undefined)
    for (const el of root.querySelectorAll<HTMLImageElement>('img[data-slot="logo"]'))
      el.src = fill.logo
}

/** One 1080 x 1350 slide as a whole HTML document; with a `fill`, in the brand theme and with the brand marks. */
export function buildSlideDocument(
  doc: TemplateDoc,
  layout: string,
  fill: SlideFill | undefined,
  fontCss: string,
): string {
  const holder = doc.root.cloneNode(false) as HTMLElement
  const source = doc.slides[layout] ?? doc.slides.title
  if (source) holder.append(source.cloneNode(true))
  if (fill) applySlideFill(holder, fill)
  return `<!doctype html><html><head><meta charset="utf-8"><style data-font-faces>${fontCss}</style><style>${doc.styles}</style><style>${BASE_STYLE}</style></head><body>${holder.outerHTML}</body></html>`
}
